import { useState, useEffect } from "react";
import "./OrderTrackingDetail.css";
import { X, CircleCheck, Package, Truck, CircleX } from "lucide-react";
import { Link, useParams, useLocation, useNavigate } from "react-router-dom";
import ProfileLayout from "../../components/profilelayout/ProfileLayout";
import ApiService from "../../components/Api_services";
import FallbackImg from "../../components/FallbackImg";

const OrderTrackingDetail = ({ order_no, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);

  const [trackingSteps, setTrackingSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const handleGoBack = () => {
    navigate(-1);
  };

  useEffect(() => {
    if (!order_no) {
      setError("Order ID is missing");
      setLoading(false);
      return;
    }
    const fetchTrackingDetails = async () => {
      try {
        setLoading(true);
        const response = await ApiService.getOrderTrackingDetail(order_no);
        if (response && response.status === 200 && response.data) {
          processTrackingData(response.data);
        } else {
          setError("Failed to load tracking details");
          setTrackingSteps([]);
        }
      } catch (err) {
        setError("An error occurred while fetching tracking details");
        setTrackingSteps([]);
      } finally {
        setLoading(false);
      }
    };
    fetchTrackingDetails();
  }, [order_no]);

  // Helper to format dates
  const formatDate = (dateStr) => {
    if (!dateStr || dateStr === "0000-00-00 00:00:00") return "";
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return "";
      return date.toLocaleDateString("en-MY", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return "";
    }
  };

  // Process tracking data from API
  const processTrackingData = (data) => {
    const steps = [];
    let currentStepIndex = 0;

    const isValidDate = (dateStr) => {
      if (!dateStr || dateStr === "0000-00-00 00:00:00") return false;
      const date = new Date(dateStr);
      return !isNaN(date.getTime());
    };

    if (data.inserted_date && isValidDate(data.inserted_date)) {
      steps.push({
        label: "Order Placed",
        date: formatDate(data.inserted_date),
        description: "Order confirmed and payment received successfully.",
      });
      currentStepIndex++;
    }
    if (data.processing_date && isValidDate(data.processing_date)) {
      steps.push({
        label: "Processing",
        date: formatDate(data.processing_date),
        description:
          "Order items are being picked, packed and prepared for dispatch.",
      });
      currentStepIndex++;
    }
    if (data.shipping_date && isValidDate(data.shipping_date)) {
      steps.push({
        label: "Shipped",
        date: formatDate(data.shipping_date),
        description:
          "Order dispatched from warehouse and currently in transit to destination.",
      });
      currentStepIndex++;
    }
    if (data.received_date && isValidDate(data.received_date)) {
      steps.push({
        label: "Delivered",
        date: formatDate(data.received_date),
        description: "Order delivered successfully to the specified location.",
      });
      currentStepIndex++;
    }
    if (data.cancel_date && isValidDate(data.cancel_date)) {
      steps.push({
        label: "Order Cancelled",
        date: formatDate(data.cancel_date),
        description: "Order has been cancelled.",
      });
      currentStepIndex++;
    }

    setTrackingSteps(steps);
    setCurrentStep(currentStepIndex);
  };

  // Show a loading message while fetching data
  if (loading) {
    return (
      <div className="trackingdetail_v2">
        <div className="trackingdetail-content_v2">
          <div className="trackingdetail-title-container_v2">
            <div className="trackingdetail-title_v2">
              <strong>TRACKING DETAILS</strong>
            </div>
          </div>
          <div className="trackingdetail-main_v2">
            <p>Loading tracking details...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show an error message if something went wrong
  if (error || trackingSteps.length === 0) {
    return (
      <div className="trackingdetail_v2">
        <div className="trackingdetail-content_v2">
          <div className="trackingdetail-title-container_v2">
            <div className="trackingdetail-title_v2">
              <strong>TRACKING DETAILS</strong>
            </div>
          </div>
          <div className="trackingdetail-main_v2">
            <p>{error || "No tracking information available yet."}</p>
          </div>
        </div>
      </div>
    );
  }

  const reversedSteps = [...trackingSteps].reverse();

  return (
    <div className="trackingdetail_v2">
      <div className="trackingdetail-content_v2">
        <div className="trackingdetail-title-container_v2">
          <div className="trackingdetail-title_v2">
            <strong>Order Tracking</strong>
          </div>

          <button className="tdback-button_v2" onClick={onClose}>
            <X />
          </button>
        </div>

        <div className="trackingdetail-main_v2">
          <div className="trackingdetail-info_v2">
            <div className="trackingdetail-infoitem_v2">
              <div>Order Number</div>
              <div className="trackingdetail-infovalue_v2">{order_no}</div>
            </div>
            <div className="trackingdetail-infoitem_v2">
              <div>Order Date</div>
              <div className="trackingdetail-infovalue_v2">
                {trackingSteps[0]?.date}
              </div>
            </div>
          </div>

          <div className="vertical-progress-tracker_v2">
            {reversedSteps.map((step, idx) => {
              const stepNum = trackingSteps.length - idx;
              return (
                <div className="vpt-step_v2" key={step.label}>
                  <div className="vpt-track_v2">
                    <div className={`vpt-circle_v2 ${step.label.replace(/\s+/g, "-").toLowerCase()}`}>
                      {step.label === "Order Placed" && <CircleCheck />}
                      {step.label === "Processing" && <Package />}
                      {step.label === "Shipped" && <Truck />}
                      {step.label === "Delivered" && <CircleCheck />}
                      {step.label === "Order Cancelled" && <CircleX />}
                    </div>
                    {idx < trackingSteps.length - 1 && (
                      <div className="vpt-line_v2"></div>
                    )}
                  </div>
                  <div className="step-content_v2">
                    <div className="step-container_v2">
                      <div className="step_v2">
                        <strong>{step.label}</strong>
                      </div>
                      <div className="step-date_v2">{step.date}</div>
                    </div>
                    <div className="step-descrip_v2">{step.description}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="trackingdetail-sub_v2">
          <button className="tdclose-button_v2" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrderTrackingDetail;
